import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert } from "@/integrations/supabase/types";
import type { ContentBriefReference, BriefReferenceInput, ContentReferenceType } from "../types";
import { getFriendlyMessage, referenceKey } from "../_shared";
import { validateBriefReferenceInputs } from "./_references";

export const createBriefReferences = async (workspaceId: string, briefId: string, references: BriefReferenceInput[]) => {
  if (!references.length) return [] as ContentBriefReference[];

  const validReferences = await validateBriefReferenceInputs(workspaceId, references);
  const { data: existingReferences, error: existingError } = await supabase
    .from("content_piece_references")
    .select("reference_type, reference_id")
    .eq("workspace_id", workspaceId)
    .eq("piece_id", briefId);

  if (existingError) throw new Error(getFriendlyMessage(existingError, "Failed to verify existing brief references"));

  const existingKeys = new Set(
    (existingReferences ?? []).map((reference) => referenceKey(reference.reference_type as ContentReferenceType, reference.reference_id)),
  );

  const payload = validReferences
    .filter((reference) => !existingKeys.has(referenceKey(reference.referenceType, reference.referenceId)))
    .map((reference, index) => ({
      workspace_id: workspaceId,
      piece_id: briefId,
      reference_type: reference.referenceType,
      reference_id: reference.referenceId,
      label_snapshot: reference.labelSnapshot,
      metadata: reference.metadata ?? {},
      sort_order: reference.sortOrder ?? index,
      ...(typeof reference.cited === "boolean" ? { cited: reference.cited } : {}),
    })) satisfies TablesInsert<"content_piece_references">[];

  if (!payload.length) return [] as ContentBriefReference[];

  const { data, error } = await supabase.from("content_piece_references").insert(payload).select("*");
  if (error) throw new Error(getFriendlyMessage(error, "Failed to attach the selected references"));
  return (data ?? []) as ContentBriefReference[];
};

export const deleteBriefReference = async (workspaceId: string, briefReferenceId: string) => {
  const { error } = await supabase
    .from("content_piece_references")
    .delete()
    .eq("workspace_id", workspaceId)
    .eq("id", briefReferenceId);

  if (error) throw new Error(getFriendlyMessage(error, "Failed to remove the reference from this brief"));
};

/**
 * Toggle whether a linked reference is treated as an in-text citation (`cited = true`)
 * or background reading that only appears in the "Works Consulted" / non-cited
 * bibliography section. See `mem://features/citations-and-affiliate-links.md`.
 */
export const updateBriefReferenceCited = async (
  workspaceId: string,
  briefReferenceId: string,
  cited: boolean,
) => {
  const { error } = await supabase
    .from("content_piece_references")
    .update({ cited })
    .eq("workspace_id", workspaceId)
    .eq("id", briefReferenceId);

  if (error) throw new Error(getFriendlyMessage(error, "Failed to update reference citation status"));
};
